/**
 * The e-mail template of ONE automation: its state, previews, history and the
 * two writes (D10).
 *
 * ONE INSTANCE PER AUTOMATION. The template tab is mounted with the
 * automation's key as its React key, so choosing another automation unmounts
 * this hook and starts a fresh one: an answer for the automation the reader
 * left resolves into an unmounted instance and changes nothing, and nothing
 * here can carry one automation's draft or preview into another's. Within one
 * instance, each kind of request has its own sequence guard, so an older
 * preview never overwrites a newer one.
 *
 * NOTHING LOADS UNTIL ASKED. `loadState` is called by the tab once it is open;
 * the history only when the reader opens it. Re-opening the tab within the same
 * automation asks nothing again — the draft lives here, and re-reading the
 * server's template underneath it would be a quiet way to lose it.
 *
 * THE SERVER'S ANSWER IS THE NEW TRUTH. A save or a revert applies exactly the
 * returned version: its number, its source, its documents. Nothing is counted
 * up locally and nothing is assumed before the answer arrives.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  createAdminAutomationTemplateVersion,
  fetchAdminAutomationTemplate,
  fetchAdminAutomationTemplateVersions,
  previewAdminAutomationTemplate,
} from "../../../api/adminData/automations";

const STATE_ERROR = "Die E-Mail-Vorlage konnte nicht geladen werden.";
const PREVIEW_ERROR = "Die Vorschau konnte nicht erstellt werden.";
const VERSIONS_ERROR = "Der Versionsverlauf konnte nicht geladen werden.";
const WRITE_ERROR = "Die Vorlage konnte nicht gespeichert werden.";

/** The error as the UI shows it: the server's sentence, its code, its issues. */
function errorOf(err, fallback) {
  return {
    message: err?.message || fallback,
    code: err?.code || null,
    status: err?.status || null,
    field: err?.field || null,
    issues: Array.isArray(err?.issues) ? err.issues : [],
  };
}

export default function useAutomationTemplate(automationKey) {
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const [state, setState] = useState({ status: "idle", data: null, error: null });
  const [preview, setPreview] = useState({
    status: "idle",
    basis: null,
    result: null,
    error: null,
  });
  const [versions, setVersions] = useState({
    status: "idle",
    items: [],
    total: 0,
    error: null,
  });
  const [write, setWrite] = useState({ status: "idle", kind: null, error: null });

  const stateSeq = useRef(0);
  const previewSeq = useRef(0);
  const versionsSeq = useRef(0);

  const loadState = useCallback(async () => {
    const mine = stateSeq.current + 1;
    stateSeq.current = mine;
    setState((prev) => ({ ...prev, status: "loading", error: null }));
    try {
      const data = await fetchAdminAutomationTemplate(automationKey);
      if (!alive.current || stateSeq.current !== mine) return null;
      setState({ status: "ready", data, error: null });
      return data;
    } catch (err) {
      if (!alive.current || stateSeq.current !== mine) return null;
      setState((prev) => ({ ...prev, status: "error", error: errorOf(err, STATE_ERROR) }));
      return null;
    }
  }, [automationKey]);

  /**
   * Render on the server. `basis` names what was rendered (the letter's
   * serialised documents, or the system template and scenario); the tab
   * compares it with what is on screen to know whether the preview is current.
   */
  const requestPreview = useCallback(
    async (request, basis) => {
      const mine = previewSeq.current + 1;
      previewSeq.current = mine;
      setPreview((prev) => ({ ...prev, status: "loading", basis, error: null }));
      try {
        const result = await previewAdminAutomationTemplate(automationKey, request);
        if (!alive.current || previewSeq.current !== mine) return;
        setPreview({ status: "success", basis, result, error: null });
      } catch (err) {
        if (!alive.current || previewSeq.current !== mine) return;
        setPreview((prev) => ({
          ...prev,
          status: "error",
          basis,
          error: errorOf(err, PREVIEW_ERROR),
        }));
      }
    },
    [automationKey]
  );

  /**
   * The reviewed preview, re-addressed to the letter that was just saved. The
   * server stored exactly the documents it rendered for the review, so their
   * rendering is the saved letter's — no second request.
   */
  const rebasePreview = useCallback((basis) => {
    setPreview((prev) => (prev.status === "success" ? { ...prev, basis } : prev));
  }, []);

  const loadVersions = useCallback(async () => {
    const mine = versionsSeq.current + 1;
    versionsSeq.current = mine;
    setVersions((prev) => ({ ...prev, status: "loading", error: null }));
    try {
      const page = await fetchAdminAutomationTemplateVersions(automationKey);
      if (!alive.current || versionsSeq.current !== mine) return;
      setVersions({
        status: "ready",
        items: Array.isArray(page?.items) ? page.items : [],
        total: Number(page?.total) || 0,
        error: null,
      });
    } catch (err) {
      if (!alive.current || versionsSeq.current !== mine) return;
      setVersions((prev) => ({ ...prev, status: "error", error: errorOf(err, VERSIONS_ERROR) }));
    }
  }, [automationKey]);

  const versionsLoaded = versions.status !== "idle";
  /** After a write: re-read the history only if the reader has it open. */
  const refreshVersionsIfLoaded = useCallback(() => {
    if (versionsLoaded) loadVersions();
  }, [versionsLoaded, loadVersions]);

  /**
   * Append a version: `{ source: "custom", subject_doc, body_doc }` to save,
   * `{ source: "system" }` to go back. Returns the server's version or `null`;
   * the error stays in `write.error`.
   */
  const createVersion = useCallback(
    async (kind, { source, subject_doc, body_doc, expected_current_version }) => {
      setWrite({ status: "pending", kind, error: null });
      try {
        const version = await createAdminAutomationTemplateVersion(automationKey, {
          source,
          subject_doc,
          body_doc,
          expected_current_version,
        });
        if (!alive.current) return null;
        setState((prev) => ({
          ...prev,
          data: prev.data
            ? {
                ...prev.data,
                effective_source: version.source,
                latest_version: version.version,
                persisted_source: version.source,
                subject_doc: version.source === "custom" ? version.subject_doc : null,
                body_doc: version.source === "custom" ? version.body_doc : null,
              }
            : prev.data,
        }));
        setWrite({ status: "idle", kind: null, error: null });
        return version;
      } catch (err) {
        if (!alive.current) return null;
        setWrite({ status: "error", kind, error: errorOf(err, WRITE_ERROR) });
        return null;
      }
    },
    [automationKey]
  );

  const clearWriteError = useCallback(() => {
    setWrite({ status: "idle", kind: null, error: null });
  }, []);

  return {
    state,
    preview,
    versions,
    write,
    loadState,
    requestPreview,
    rebasePreview,
    loadVersions,
    refreshVersionsIfLoaded,
    createVersion,
    clearWriteError,
  };
}
